import { useState, useEffect } from 'react'

import Cookies from 'js-cookie'

import { AddonOrderRatingData } from '@client/components'
import { useConfig } from '@client/contexts/ConfigProvider'
import { OrderDirectionEnum, OrderOrderEnum, OrderOriginEnum, OrderStatusEnum } from '@uctypes/api/globalTypes'
import { OrderItemFragment } from '@uctypes/api/OrderItemFragment'

import { useOrdersQuery } from './api'

interface ReturnType {
  addOnsRatingModalOpen: boolean
  addOnsRatingOrder: { [k: string]: AddonOrderRatingData } | null
  loading: boolean
}

function getOneMonthAgoDate(): string {
  const today = new Date()
  const year = today.getFullYear()
  let month = today.getMonth() // 0-based
  const day = String(today.getDate()).padStart(2, '0')
  let resultYear = year
  if (month === 0) {
    month = 11
    resultYear = year - 1
  } else {
    month -= 1
  }
  const monthStr = String(month + 1).padStart(2, '0')
  return `${resultYear}-${monthStr}-${day}`
}

function calculateDifferenceInDays(date1: Date, date2: Date): number {
  const millisecondsPerDay = 1000 * 60 * 60 * 24
  const timeDiff = Math.abs(date1.getTime() - date2.getTime())
  return Math.floor(timeDiff / millisecondsPerDay)
}

function shouldIncludeForAddOnRating(orderItem: OrderItemFragment): boolean {
  if (!orderItem) return false
  const isMealKitType = [
    'MealKit',
    'MealKitPlan',
    'MealKitSubscriptionFee',
  ].includes(orderItem.product?.__typename)
  const isGiftOrFee = orderItem.product?.name?.includes('Gift') || orderItem.product?.name?.includes('Fee')
  const isFixedSubscriptionFee = orderItem.product?.name === 'Fixed Subscription Fee'
  const isRated = !!orderItem.orderItemRating?.value
  const isGroupRated = !!orderItem.isProductGroupRated
  const dispatchDate = new Date(orderItem.order.dispatchDate)
  const today = new Date()
  const differenceInDays = calculateDifferenceInDays(today, dispatchDate)
  return (
    !isRated &&
    !isMealKitType &&
    !isGiftOrFee &&
    !isFixedSubscriptionFee &&
    differenceInDays > 30 &&
    !isGroupRated
  )
}

export const useAddOnsRatingsLogic = (userId: string, isRegistered: boolean): ReturnType => {
  const config = useConfig()
  const [isClient, setIsClient] = useState(false)

  // Ensure this logic only runs on the client
  useEffect(() => {
    setIsClient(true)
  }, [])

  // On SSR (not client), assume add-ons ratings are dismissed to avoid showing modal unexpectedly.
  // Change to 'false' if you want modal to show by default on SSR.
  const isAddonsDismissed = isClient ? Cookies.get('isAddOnsDismissed') : true
  const oneMonthAgo = getOneMonthAgoDate()

  // Only fetch if user is registered, not dismissed, has a userId, and we are on the client
  const shouldSkip = !userId || !!isAddonsDismissed || !isRegistered || !isClient

  const { data: ordersAddOnDataAll, loading: queryLoading } = useOrdersQuery({
    context: { key: 'uniqueKeyForParentQuery' },
    skip: shouldSkip,
    ssr: config.fetchSSRQuery(),
    variables: {
      filters: {
        users: [userId],
        status: [OrderStatusEnum.COMPLETE],
        origin: [OrderOriginEnum.NODE],
        maxCreatedAt: oneMonthAgo,
      },
      order: {
        field: OrderOrderEnum.DISPATCH_DATE,
        direction: OrderDirectionEnum.DESC,
      },
      skip: 0,
      limit: 4,
    },
  })

  const isLoading = queryLoading || !isClient

  if (isLoading) {
    return {
      addOnsRatingModalOpen: false,
      addOnsRatingOrder: null,
      loading: true,
    }
  }

  const ordersList = ordersAddOnDataAll?.orders?.list || []

  /**
   * Collects all add-on items to be rated from eligible orders.
   */
  function getAllAddOnItemsToBeRated(): { [key: string]: AddonOrderRatingData } {
    const addOnItemsToBeRated: { [key: string]: AddonOrderRatingData } = {}
    for (const order of ordersList) {
      for (const orderItem of order.orderItems) {
        if (shouldIncludeForAddOnRating(orderItem as unknown as OrderItemFragment)) {
          const productId = orderItem.product?.id
          const orderId = order.id
          if (productId && orderId) {
            addOnItemsToBeRated[productId + orderId] = { orderId, orderItemData: orderItem }
          }
        }
      }
    }
    return addOnItemsToBeRated
  }

  const addOnsToBeRated = getAllAddOnItemsToBeRated()

  return ordersList.length > 0 && !isAddonsDismissed && Object.keys(addOnsToBeRated).length > 0
    ? {
      addOnsRatingModalOpen: true,
      addOnsRatingOrder: addOnsToBeRated,
      loading: false,
    }
    : {
      addOnsRatingModalOpen: false,
      addOnsRatingOrder: null,
      loading: false,
    }
}
